import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import type { Updater } from 'use-immer';
import { Scrollbars } from 'react-custom-scrollbars';
import { type EffectRain, EffectRainType, ImageType } from '@types';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import flexBoxHack from '@utils/flexBoxHack';
import Slider from '@components/Slider';
import Tip from '@components/Tip';
import Button from '@components/Button';
import { Validator } from '@components/Validator';
import Checkbox from '@components/Checkbox';
import {
  type EffectDefault,
  type EffectRainParams,
  RAIN_TYPE_CONFIG,
} from '../constants';
import { krpAddRainEffect, krpRemoveEffect } from '../actions/krpEffects';
import { selectLibImages } from '../../mediaLib';

interface Props {
  state: EffectRain;
  setState: Updater<EffectRain>;
}

type TypeId = 'sys' | 'custom';
const typeItems: DropdownItem<TypeId>[] = [
  { id: 'sys', name: '系统特效' },
  { id: 'custom', name: '自定义' },
];
const iconList = [
  EffectRainType.rain,
  EffectRainType.snowFlower,
  EffectRainType.snow,
  EffectRainType.bubble,
  EffectRainType.heart,
  EffectRainType.packet,
  EffectRainType.star,
  EffectRainType.smile,
  EffectRainType.money,
];
const invertItems: DropdownItem<EffectRain['invert']>[] = [
  { id: 0, name: '下降' },
  { id: 1, name: '上升' },
];

export function PanelEffectRain({ state, setState }: Props) {
  useEffect(() => {
    krpAddRainEffect(state);
  }, [state]);

  useEffect(() => {
    return () => {
      krpRemoveEffect(state.id);
    };
  }, []);

  const showSetting = !(
    state.rainType === EffectRainType.custom && !state.mediaUrl
  );

  const preset: Record<keyof EffectDefault, boolean> = useMemo(() => {
    const result = { sm: true, md: true, lg: true };
    const settings = RAIN_TYPE_CONFIG[state.rainType].defaultSetting;
    const keys = Object.keys(settings.sm);
    keys.forEach((key: keyof typeof settings.sm) => {
      if (state[key] !== settings.sm[key]) result.sm = false;
      if (state[key] !== settings.md[key]) result.md = false;
      if (state[key] !== settings.lg[key]) result.lg = false;
    });
    return result;
  }, [state]);

  const handleChangePreset = (
    size: keyof EffectDefault,
    rainType: EffectRainType = state.rainType
  ) => {
    const settings = RAIN_TYPE_CONFIG[rainType].defaultSetting;
    const keys = Object.keys(settings[size]);
    keys.forEach((key: keyof EffectRainParams) => {
      setState((draft) => {
        draft[key] = settings[size][key];
      });
    });
  };

  const handleTypeChange = (type: TypeId) => {
    if (type === 'sys') {
      setState((draft) => {
        draft.rainType = iconList[0];
        draft.mediaId = 0;
        draft.mediaUrl = '';
      });
      handleChangePreset('sm', EffectRainType.rain);
    } else {
      setState((draft) => {
        draft.rainType = EffectRainType.custom;
      });
      handleChangePreset('sm', EffectRainType.custom);
    }
  };

  const handleImageSelect = async () => {
    const { libItems } = await selectLibImages();
    if (!libItems) return;
    setState((draft) => {
      draft.mediaId = libItems[0].id;
      draft.mediaUrl = libItems[0].url;
    });
  };

  const renderSys = () => {
    return (
      <ImageContainer className="h-140">
        <Scrollbars>
          <div className="flex flex-wrap items-center justify-between p-5">
            {iconList.map((iconId) => {
              const { icon, id } = RAIN_TYPE_CONFIG[iconId];
              return (
                <Image
                  key={id}
                  className={classNames('mb-10 h-45 w-45 cursor-pointer', {
                    'ring-2 ring-inset ring-orange': iconId === state.rainType,
                  })}
                  src={icon}
                  type={ImageType.sysMedia}
                  onClick={() => {
                    setState((draft) => {
                      draft.rainType = iconId;
                    });
                    handleChangePreset('sm', iconId);
                  }}
                />
              );
            })}
            {flexBoxHack(45, 4)}
          </div>
        </Scrollbars>
      </ImageContainer>
    );
  };

  const renderCustom = () => {
    return (
      <>
        <Validator
          value={state.mediaUrl}
          validFn={(value) => !!value}
          warningMessage="请选择特效图片"
        >
          {({ value, warning }) => (
            <ImageContainer
              placeholder="请选择PNG图片"
              className="h-115"
              warning={warning}
            >
              {!!value && (
                <Image
                  src={value}
                  type={ImageType.media}
                  className="max-h-full max-w-full"
                />
              )}
            </ImageContainer>
          )}
        </Validator>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸：50x50</Tip>
          <Button size="sm" onClick={handleImageSelect}>
            选择图片
          </Button>
        </div>
      </>
    );
  };

  const renderSlider = (params: {
    dataKey: keyof Pick<
      EffectRain,
      | 'scale'
      | 'flakes'
      | 'floor'
      | 'speed'
      | 'shake'
      | 'speedVariance'
      | 'wind'
      | 'windDir'
    >;
    name: string;
    min: number;
    max: number;
    step: number;
  }) => {
    const { dataKey, name, min, max, step } = params;
    return (
      <Slider
        min={min}
        max={max}
        className="mt-20"
        value={state[dataKey]}
        onChange={(val) =>
          setState((draft) => {
            draft[dataKey] = val;
          })
        }
      >
        <Slider.Header title={name} />
        <Slider.Bar step={step} />
        <Slider.Control step={step} />
      </Slider>
    );
  };

  const renderSetting = () => {
    return (
      <>
        <Collapse title="特效力度">
          <div className="flex items-center justify-between">
            <Checkbox
              label="小"
              checked={preset.sm}
              onClick={() => handleChangePreset('sm')}
            />
            <Checkbox
              label="中"
              checked={preset.md}
              onClick={() => handleChangePreset('md')}
            />
            <Checkbox
              label="大"
              checked={preset.lg}
              onClick={() => handleChangePreset('lg')}
            />
          </div>
        </Collapse>
        <Collapse title="高级参数设置" isHideDefault>
          <div className="mb-10">运动方向</div>
          <Dropdown
            className="mb-20"
            items={invertItems}
            selected={state.invert}
            onChange={(val) =>
              setState((draft) => {
                draft.invert = val;
              })
            }
          />
          {renderSlider({
            min: 0.2,
            max: 1,
            step: 0.01,
            dataKey: 'scale',
            name: '特效缩放',
          })}
          {renderSlider({
            min: 1,
            max: 8000,
            step: 1,
            dataKey: 'flakes',
            name: '特效数量',
          })}
          {renderSlider({
            min: 0,
            max: 10,
            step: 0.1,
            dataKey: 'speed',
            name: '运动速度',
          })}
          {renderSlider({
            min: 0,
            max: 100,
            step: 1,
            dataKey: 'speedVariance',
            name: '运动速度变化',
          })}
          {renderSlider({
            min: 0,
            max: 1,
            step: 0.01,
            dataKey: 'floor',
            name: '地面高度',
          })}
          {renderSlider({
            min: 0,
            max: 30,
            step: 0.1,
            dataKey: 'shake',
            name: '随机抖动',
          })}
          {renderSlider({
            min: 0,
            max: 30,
            step: 0.01,
            dataKey: 'wind',
            name: '风力',
          })}
          {renderSlider({
            min: 0,
            max: 360,
            step: 1,
            dataKey: 'windDir',
            name: '风向',
          })}
        </Collapse>
      </>
    );
  };

  return (
    <>
      <Collapse title="特效选择">
        <Dropdown
          className="mb-20"
          items={typeItems}
          selected={state.rainType === EffectRainType.custom ? 'custom' : 'sys'}
          onChange={handleTypeChange}
        />
        {state.rainType !== EffectRainType.custom && renderSys()}
        {state.rainType === EffectRainType.custom && renderCustom()}
      </Collapse>
      {showSetting && renderSetting()}
    </>
  );
}
